// Client schema for the PowerSync local database.
// Ported 1:1 from NativePropel lib/models/schema.dart (commit 3038fb5) so the
// web app reads exactly what the phones read. Keep in lockstep with the app
// and PropelRails sync-rules.yaml. Tables the web never writes are still
// declared so sync can populate them.
import { column, Schema, Table } from "@powersync/web";

const users = new Table({
    email: column.text,
    encrypted_password: column.text,
    reset_password_token: column.text,
    reset_password_sent_at: column.text,
    remember_created_at: column.text,
    created_at: column.text,
    updated_at: column.text,
    contact_number: column.text,
    sign_in_count: column.integer,
    last_sign_in_at: column.text,
    current_sign_in_at: column.text,
    last_sign_in_ip: column.text,
    current_sign_in_ip: column.text,
    name: column.text,
    last_name: column.text,
    whatsapp_context: column.text,
    whatsapp_context_metadata: column.text,
    default_team_id: column.text,
    language_preference: column.text,
    regulatory_credentials: column.text,
    email_scheduled_reminders: column.integer,
    email_reports_completed: column.integer,
    email_reports_exported: column.integer,
    super_admin: column.integer,
    timezone: column.text,
});

const map_features = new Table({
    label: column.text,
    description: column.text,
    created_at: column.text,
    updated_at: column.text,
    geometry: column.text,
    colour: column.text,
    geometry_type: column.text,
    map_id: column.text,
    user_id: column.text,
    feature_category_id: column.text,
    latitude: column.text,
    longitude: column.text,
    map_feature_status_id: column.text,
    responsible_user_id: column.text,
    granted_user_ids: column.text,
    admin_user_ids: column.text,
    geom: column.text,
    level: column.text,
});

const team_memberships = new Table({
    role: column.text,
    created_at: column.text,
    updated_at: column.text,
    user_id: column.text,
    team_id: column.text,
});

const field_notes = new Table({
    title: column.text,
    user_id: column.text,
    map_id: column.text,
    map_feature_id: column.text,
    started_at: column.text,
    completed_at: column.text,
    created_at: column.text,
    updated_at: column.text,
    ai_summary: column.text,
    ai_processed: column.integer,
    audio_summary_processed: column.integer,
    audio_summary_text: column.text,
    processing_state: column.text,
    personal_note_id: column.text,
    media_processing_completed_at: column.text,
    transcriptions_completed_at: column.text,
    ai_summary_completed_at: column.text,
    reminders_extraction_completed_at: column.text,
    metrics_extraction_completed_at: column.text,
    failed_media_message_ids: column.text,
    failed_transcription_message_ids: column.text,
    temp_id: column.text,
    report_generation_queued_at: column.text,
    original_ai_summary: column.text,
    learning_suggestions: column.text,
    image_text_extraction_completed_at: column.text,
    weather_alerts_extraction_completed_at: column.text,
    comments_extraction_completed_at: column.text,
    partner_overloaded: column.integer,
    orphan_context: column.text,
});

const mobile_field_notes = new Table({
    title: column.text,
    user_id: column.text,
    map_id: column.text,
    map_feature_id: column.text,
    started_at: column.text,
    completed_at: column.text,
    created_at: column.text,
    updated_at: column.text,
    ai_summary: column.text,
    ai_processed: column.integer,
    audio_summary_processed: column.integer,
    audio_summary_text: column.text,
    processing_state: column.text,
    personal_note_id: column.text,
    media_processing_completed_at: column.text,
    transcriptions_completed_at: column.text,
    ai_summary_completed_at: column.text,
    reminders_extraction_completed_at: column.text,
    metrics_extraction_completed_at: column.text,
    failed_media_message_ids: column.text,
    failed_transcription_message_ids: column.text,
    temp_id: column.text,
    report_generation_queued_at: column.text,
    original_ai_summary: column.text,
    learning_suggestions: column.text,
    image_text_extraction_completed_at: column.text,
    weather_alerts_extraction_completed_at: column.text,
    comments_extraction_completed_at: column.text,
    partner_overloaded: column.integer,
    orphan_context: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const feature_prep_notes = new Table({
    summary_text: column.text,
    map_feature_id: column.text,
    user_id: column.text,
    viewed: column.integer,
    created_at: column.text,
    updated_at: column.text,
});

const notifications = new Table({
    user_id: column.text,
    notifiable_type: column.text,
    notifiable_id: column.text,
    notification_type: column.text,
    title: column.text,
    message: column.text,
    read_at: column.text,
    created_at: column.text,
    updated_at: column.text,
});

const local_notifications = new Table({
    user_id: column.text,
    notifiable_type: column.text,
    notifiable_id: column.text,
    notification_type: column.text,
    title: column.text,
    message: column.text,
    read_at: column.text,
    created_at: column.text,
    updated_at: column.text,
}, { localOnly: true });

const mobile_farm_reports = new Table({
    map_feature_id: column.text,
    user_id: column.text,
    title: column.text,
    report_date: column.text,
    field_note_ids: column.text,
    include_summary: column.integer,
    one_page_only: column.integer,
    include_appendices: column.integer,
    status: column.text,
    generated_at: column.text,
    created_at: column.text,
    updated_at: column.text,
    ai_summary: column.text,
    error_message: column.text,
    report_language: column.text,
    approved_at: column.text,
    approved_by_id: column.text,
    approver_name: column.text,
    signature_data: column.text,
    report_date_start: column.text,
    report_date_end: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const mobile_farm_report_downloads = new Table({
    attachment_id: column.text,
    farm_report_id: column.text,
    map_feature_id: column.text,
    storage_key: column.text,
    filename: column.text,
    content_type: column.text,
    byte_size: column.integer,
    created_at: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const mobile_feature_contacts = new Table({
    map_feature_id: column.text,
    client_contact_id: column.text,
    notes: column.text,
    created_at: column.text,
    updated_at: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const mobile_feature_inbox_messages = new Table({
    inbox_message_id: column.text,
    map_feature_id: column.text,
    created_at: column.text,
    updated_at: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const teams = new Table({
    name: column.text,
    created_at: column.text,
    updated_at: column.text,
    mailbox_slug: column.text,
    footer: column.text,
    trial_ends_at: column.text,
    trial_starts_at: column.text,
    trial_active: column.integer,
    seasons_enabled: column.integer,
    llm_agents_enabled: column.integer,
    report_heading_color: column.text,
    weather_unit_group: column.text,
    vision_extraction_enabled: column.integer,
    weather_monitoring_enabled: column.integer,
    client_finder_enabled: column.integer,
    letterhead_top_margin: column.integer,
    letterhead_bottom_margin: column.integer,
    report_font: column.text,
    data_exported_at: column.text,
    level_hierarchy: column.text,
    hierarchy_capture_enabled: column.integer,
});

const maps = new Table({
    name: column.text,
    description: column.text,
    created_at: column.text,
    updated_at: column.text,
    team_id: column.text,
    user_id: column.text,
});

const feature_categories = new Table({
    name: column.text,
    icon: column.text,
    created_at: column.text,
    updated_at: column.text,
    team_id: column.text,
    material_icon: column.text,
});

const map_feature_statuses = new Table({
    name: column.text,
    colour: column.text,
    is_default: column.integer,
    team_id: column.text,
    created_at: column.text,
    updated_at: column.text,
});

const client_contacts = new Table({
    name: column.text,
    email: column.text,
    phone: column.text,
    role: column.text,
    notes: column.text,
    team_id: column.text,
    user_id: column.text,
    created_at: column.text,
    updated_at: column.text,
});

const mobile_reminders = new Table({
    details: column.text,
    reminder_date: column.text,
    reminder_time: column.text,
    created_at: column.text,
    updated_at: column.text,
    sent: column.integer,
    creator_user_id: column.text,
    assigned_user_id: column.text,
    map_feature_id: column.text,
    reminder_type: column.integer,
    completed_at: column.text,
    completed_by_id: column.text,
    canceled_at: column.text,
    canceled_by_id: column.text,
    status: column.integer,
    ai_created: column.integer,
    field_note_id: column.text,
});

const mobile_field_note_media = new Table({
    attachment_id: column.text,
    whatsapp_message_id: column.text,
    field_note_id: column.text,
    map_feature_id: column.text,
    storage_key: column.text,
    filename: column.text,
    content_type: column.text,
    byte_size: column.integer,
    created_at: column.text,
    body: column.text,
    user_id: column.text,
    message_sid: column.text,
    message_status: column.text,
    media_count: column.integer,
    processed: column.integer,
    updated_at: column.text,
    latitude: column.text,
    longitude: column.text,
    address: column.text,
    location_label: column.text,
    whatsapp_message_content_ai: column.text,
    media_data: column.text,
    media_processed_count: column.integer,
    personal_note_id: column.text,
    raw_transcript: column.text,
    image_text_ai: column.text,
    visit_form_id: column.text,
    entity_id: column.text,
    metric_id: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
    tagged_map_feature_id: column.text,
    tagging_orphan_context: column.text,
});

const mobile_field_note_attachments = new Table({
    attachment_id: column.text,
    field_note_id: column.text,
    map_feature_id: column.text,
    storage_key: column.text,
    filename: column.text,
    content_type: column.text,
    byte_size: column.integer,
    attachment_name: column.text,
    created_at: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const mobile_custom_report_statuses = new Table({
    field_note_id: column.text,
    status: column.text,
    config_name: column.text,
    created_at: column.text,
    generated_at: column.text,
    map_feature_id: column.text,
    report_data: column.text,
    signature_data: column.text,
    approved_at: column.text,
    approver_name: column.text,
    language_used: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const mobile_custom_report_downloads = new Table({
    attachment_id: column.text,
    report_id: column.text,
    storage_key: column.text,
    filename: column.text,
    content_type: column.text,
    byte_size: column.integer,
    map_feature_id: column.text,
    created_at: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const mobile_metrics = new Table({
    value: column.text,
    created_at: column.text,
    updated_at: column.text,
    map_feature_id: column.text,
    metric_type_id: column.text,
    metric_type_option_id: column.text,
    ai_created: column.integer,
    field_note_id: column.text,
    extracted_text: column.text,
    ai_reasoning: column.text,
    visit_form_id: column.text,
    entity_id: column.text,
    value_source: column.integer,
    latitude: column.real,
    longitude: column.real,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const metric_types = new Table({
    name: column.text,
    data_type: column.text,
    created_at: column.text,
    updated_at: column.text,
    team_id: column.text,
    min_value: column.text,
    max_value: column.text,
    target_value: column.text,
    required_for_each_field_note: column.integer,
    metric_type_group_id: column.text,
});

const metric_type_groups = new Table({
    name: column.text,
    description: column.text,
    color: column.text,
    team_id: column.text,
    sort_order: column.integer,
    created_at: column.text,
    updated_at: column.text,
});

const visit_form_templates = new Table({
    name: column.text,
    description: column.text,
    form_mode: column.integer,
    entity_type_id: column.text,
    team_id: column.text,
    active: column.integer,
    created_at: column.text,
    updated_at: column.text,
});

const mobile_metric_type_options = new Table({
    value: column.text,
    metric_type_id: column.text,
    created_at: column.text,
    updated_at: column.text,
    team_id: column.text,
});

const mobile_visit_form_template_metrics = new Table({
    visit_form_template_id: column.text,
    metric_type_id: column.text,
    position: column.integer,
    required: column.integer,
    description: column.text,
    multi_entry: column.integer,
    max_entries: column.integer,
    created_at: column.text,
    updated_at: column.text,
    team_id: column.text,
});

const entity_types = new Table({
    name: column.text,
    icon: column.text,
    team_id: column.text,
    created_at: column.text,
    updated_at: column.text,
});

const mobile_entities = new Table({
    identifier: column.text,
    entity_type_id: column.text,
    map_feature_id: column.text,
    geometry: column.text,
    geometry_type: column.text,
    created_at: column.text,
    updated_at: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const mobile_visit_forms = new Table({
    visit_form_template_id: column.text,
    field_note_id: column.text,
    entity_id: column.text,
    submitted_at: column.text,
    created_at: column.text,
    updated_at: column.text,
    map_feature_id: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const field_note_checklists = new Table({
    name: column.text,
    body: column.text,
    team_id: column.text,
    created_at: column.text,
    updated_at: column.text,
});

const mobile_zone_memberships = new Table({
    zone_id: column.text,
    member_id: column.text,
    notes: column.text,
    created_at: column.text,
    updated_at: column.text,
    team_id: column.text,
});

const visit_forms = new Table({
    visit_form_template_id: column.text,
    field_note_id: column.text,
    entity_id: column.text,
    submitted_at: column.text,
    created_at: column.text,
    updated_at: column.text,
}, { localOnly: true });

const pending_message_deletions = new Table({
    field_note_id: column.text,
    whatsapp_message_id: column.text,
    created_at: column.text,
});

const pending_message_uploads = new Table({
    field_note_id: column.text,
    visit_form_id: column.text,
    created_at: column.text,
});

const team_report_configs = new Table({
    name: column.text,
    team_id: column.text,
    config_data: column.text,
    is_default: column.integer,
    active: column.integer,
    created_at: column.text,
    updated_at: column.text,
});

const form_wizard_drafts = new Table({
    field_note_id: column.text,
    visit_form_template_id: column.text,
    visit_form_id: column.text,
    selected_entity_id: column.text,
    wizard_step: column.integer,
    metric_values_json: column.text,
    metric_draft_json: column.text,
    pre_generated_metric_ids_json: column.text,
    entity_drafts_json: column.text,
    used_entities_json: column.text,
    created_at: column.text,
    updated_at: column.text,
}, { localOnly: true });

const mobile_map_feature_comments = new Table({
    content: column.text,
    map_feature_id: column.text,
    user_id: column.text,
    mentioned_user_id: column.text,
    field_note_id: column.text,
    ai_generated: column.integer,
    status: column.text,
    created_at: column.text,
    updated_at: column.text,
    mf_user_id: column.text,
    mf_responsible_user_id: column.text,
    mf_granted_user_ids: column.text,
    mf_admin_user_ids: column.text,
});

const mobile_team_users = new Table({
    user_id: column.text,
    team_id: column.text,
    name: column.text,
    last_name: column.text,
    email: column.text,
});

const map_feature_access_commands = new Table({
    action: column.text,
    map_feature_id: column.text,
    user_id: column.text,
    created_at: column.text,
});

export const AppSchema = new Schema({
  users,
  map_features,
  team_memberships,
  field_notes,
  mobile_field_notes,
  feature_prep_notes,
  notifications,
  local_notifications,
  mobile_farm_reports,
  mobile_farm_report_downloads,
  mobile_feature_contacts,
  mobile_feature_inbox_messages,
  teams,
  maps,
  feature_categories,
  map_feature_statuses,
  client_contacts,
  mobile_reminders,
  mobile_field_note_media,
  mobile_field_note_attachments,
  mobile_custom_report_statuses,
  mobile_custom_report_downloads,
  mobile_metrics,
  metric_types,
  metric_type_groups,
  visit_form_templates,
  mobile_metric_type_options,
  mobile_visit_form_template_metrics,
  entity_types,
  mobile_entities,
  mobile_visit_forms,
  field_note_checklists,
  mobile_zone_memberships,
  visit_forms,
  pending_message_deletions,
  pending_message_uploads,
  team_report_configs,
  form_wizard_drafts,
  mobile_map_feature_comments,
  mobile_team_users,
  map_feature_access_commands,
});

export type Database = (typeof AppSchema)["types"];
